import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import * as Yup from 'yup';

import { Container, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  HeaderBreadcrumbs,
  useErrorSnackbar,
} from '@pflegenavi/web-components';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { usePreviewImageIds } from '../components/receipt-batch/utils/usePreviewImageIds';
import { renderReceiptBatchCommonFormNew } from '../components/receipt-batch/ReceiptBatchCommonForm';
import type { ReceiptBatchFormValues } from '../components/receipt-batch/interfaces/ReceiptBatchFormValues';
import { useServiceProviderRenderReceiptBatchRow } from '../components/receipt-batch/useServiceProviderRenderReceiptBatchRow';
import {
  useGetNursingHomeList,
  usePostReceiptBatchPhoenix,
} from '@pflegenavi/service-provider-frontend/api';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import { getGlobalServiceProviderIdFromToken } from '../utils/getGlobalServiceProviderIdFromToken';
import { renderReceiptBatchImageInput } from '../components/receipt-batch/ReceiptBatchImageInput';
import NoRecordsSvg from '../assets/new_invite_pic.svg';
import { ConnectionStatus } from '@pflegenavi/service-provider-shared/api-endpoints';
import { ServiceProviderReceiptBatchForm } from '../components/receipt-batch/form/ServiceProviderReceiptBatchForm';
import { createNewReceipt } from '../components/receipt-batch/form/ServiceProviderReceiptBatchReceiptsView';
import {
  ReceiptBatchProvider,
  useReceiptBatchStore,
} from '../components/receipt-batch/ReceiptBatchContext';
import { useShallow } from 'zustand/react/shallow';
import { useTaxItemsAndDefault } from '../components/receipt-batch/useDefaultTaxItem';
import { transformReceiptBatchFormValuesToUpdateReceiptBatchPhoenix } from '../functions/transformReceiptBatchFormValuesToUpdateReceiptBatchPhoenix';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface AddReceiptBatchPageProps {}

const AddReceiptBatchPageBase: FC<AddReceiptBatchPageProps> = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { enqueueErrorSnackbar } = useErrorSnackbar();
  const navigate = useNavigate();
  // @ts-expect-error // getToken is intentionally hidden. It is necessary here to exctract the globalServiceProviderId
  const { getToken } = useAuthentication();
  const { mutateAsync: addReceiptBatchPhoenix } = usePostReceiptBatchPhoenix();

  const [allowNavigateAway, setAllowNavigateAway] = useState(false);

  const [defaultAutoGeneratedReceipts] = useReceiptBatchStore(
    useShallow((state) => [
      state.serviceProvider?.defaultAutoGenerateReceipts ?? false,
    ])
  );

  const handleSubmit = useCallback(
    async (values: ReceiptBatchFormValues) => {
      const token = await getToken();
      const globalServiceProviderId =
        getGlobalServiceProviderIdFromToken(token);
      if (
        (!values.individualDatesMode && !values.receiptDate) ||
        !values.nursingHomeId ||
        !globalServiceProviderId
      ) {
        throw new Error('Missing required fields');
      }
      try {
        setAllowNavigateAway(true);
        const newBatch = await addReceiptBatchPhoenix(
          transformReceiptBatchFormValuesToUpdateReceiptBatchPhoenix(values)
        );
        const newBatchId = newBatch.id;
        enqueueSnackbar(t('receipts.batch.form.updated-successfully'), {
          variant: 'success',
        });
        navigate(
          spAppMainPages.RECEIPT_BATCH_EDIT.replace(':batchId', newBatchId)
        );
      } catch (e) {
        enqueueErrorSnackbar(e, t('receipts.batch.form.failed-to-update'), {
          variant: 'error',
        });
        setAllowNavigateAway(false);
      }
    },
    [
      getToken,
      enqueueSnackbar,
      t,
      navigate,
      addReceiptBatchPhoenix,
      enqueueErrorSnackbar,
    ]
  );

  const validationSchemaExtension = useMemo(() => {
    return Yup.object({
      nursingHomeId: Yup.string().required(
        t('service-provider-app.please-selected-a-nursing-home')
      ),
      tenant: Yup.string(),
    });
  }, [t]);

  const { defaultTaxCode } = useTaxItemsAndDefault();

  const initialReceipts = useMemo(
    () =>
      Array(1).fill(
        createNewReceipt({
          lineItemOpts: {
            taxes: defaultTaxCode ? [defaultTaxCode] : undefined,
          },
        })
      ),
    [defaultTaxCode]
  );

  const { data: nursingHomes } = useGetNursingHomeList();
  const activeNursingHomes = useMemo(
    () =>
      (nursingHomes ?? []).filter(
        (n) => n.connectionStatus === ConnectionStatus.Active
      ),
    [nursingHomes]
  );

  if (activeNursingHomes?.length === 0) {
    return (
      <Container sx={{ maxWidth: 1400 }}>
        <AddReceiptBatchPageBreadcrumbs />
        <Stack
          sx={{ textAlign: 'center' }}
          justifyContent="center"
          alignItems="center"
          gap={2}
        >
          <img alt={t('emptyContent-img')} src={NoRecordsSvg} width={450} />
          <Typography variant="body1" fontWeight="700" sx={{ mb: 2 }}>
            {t('sp-web-app.no-nursing-homes')}
          </Typography>
        </Stack>
      </Container>
    );
  }

  return (
    <Container sx={{ maxWidth: 1400 }}>
      <AddReceiptBatchPageBreadcrumbs />
      <ServiceProviderReceiptBatchForm<ReceiptBatchFormValues>
        initialValues={{
          receiptDate: undefined,
          nursingHomeId: undefined,
          tenant: undefined,
          batchImageIds: [],
          receipts: initialReceipts,
          individualDatesMode: false,
          generateReceipts: defaultAutoGeneratedReceipts,
        }}
        validationSchemaExtension={validationSchemaExtension}
        allowNavigateAway={allowNavigateAway}
        handleSubmit={handleSubmit}
        usePreviewImageIds={usePreviewImageIds}
        renderReceiptBatchCommonForm={renderReceiptBatchCommonFormNew}
        renderReceiptBatchImageInput={renderReceiptBatchImageInput}
        useRenderReceiptBatchRow={useServiceProviderRenderReceiptBatchRow}
      />
    </Container>
  );
};

const AddReceiptBatchPageBreadcrumbs = () => {
  const { t } = useTranslation();
  return (
    <HeaderBreadcrumbs
      heading={t('dashboard.main')}
      links={[
        {
          name: t('dashboard.main'),
          href: spAppMainPages.DASHBOARD,
        },
        { name: t('sp-web-app.receipt-batch.new') },
      ]}
      sx={{
        pb: 0,
      }}
    />
  );
};

export const AddReceiptBatchPage = (): JSX.Element => {
  return (
    <ReceiptBatchProvider>
      <AddReceiptBatchPageBase />
    </ReceiptBatchProvider>
  );
};
